import { devError } from '@/lib/logger';
import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Camera, Trash2, Eraser, Search, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { format, subDays } from 'date-fns';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTION_LABELS = {
  clock_in: 'Entrada',
  clock_out: 'Saída',
  break_start: 'Início Pausa',
  break_end: 'Fim Pausa',
};

export default function Photos() {
  const [locations, setLocations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);

  const [filterLocation, setFilterLocation] = useState('all');
  const [filterEmployee, setFilterEmployee] = useState('all');
  const [filterAction, setFilterAction] = useState('all');
  const [startDate, setStartDate] = useState(format(subDays(new Date(), 7), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));

  const [previewPhoto, setPreviewPhoto] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [locRes, empRes] = await Promise.all([
          axios.get(`${API}/locations`, { withCredentials: true }),
          axios.get(`${API}/employees?status=active`, { withCredentials: true }),
        ]);
        setLocations(locRes.data);
        setEmployees(empRes.data);
      } catch (err) {
        devError('photos-init', err);
      }
    })();
  }, []);

  const fetchPhotos = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (filterLocation !== 'all') params.location_id = filterLocation;
      if (filterEmployee !== 'all') params.employee_id = filterEmployee;
      if (filterAction !== 'all') params.action = filterAction;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const res = await axios.get(`${API}/clock-photos`, {
        params,
        withCredentials: true,
      });
      setPhotos(res.data.photos || []);
    } catch (err) {
      devError('photos-fetch', err);
      toast.error('Erro ao carregar fotos');
    } finally {
      setLoading(false);
    }
  }, [filterLocation, filterEmployee, filterAction, startDate, endDate]);

  useEffect(() => {
    fetchPhotos();
  }, [fetchPhotos]);

  const handleDelete = async (photoId) => {
    if (!window.confirm('Apagar esta foto definitivamente? Esta ação não pode ser desfeita.')) return;
    try {
      await axios.delete(`${API}/clock-photos/${photoId}`, { withCredentials: true });
      toast.success('Foto apagada');
      setPhotos((prev) => prev.filter((p) => p.photo_id !== photoId));
      if (previewPhoto?.photo_id === photoId) setPreviewPhoto(null);
    } catch (err) {
      devError('photos-delete', err);
      toast.error('Erro ao apagar foto');
    }
  };

  const handleCleanup = async () => {
    if (!window.confirm('Executar limpeza RGPD agora? Todas as fotos com mais de 90 dias serão apagadas.')) return;
    try {
      const res = await axios.delete(`${API}/clock-photos/cleanup`, { withCredentials: true });
      toast.success(`Limpeza concluída: ${res.data.deleted} foto(s) apagada(s)`);
      fetchPhotos();
    } catch (err) {
      devError('photos-cleanup', err);
      toast.error('Erro na limpeza');
    }
  };

  const formatDate = (iso) => {
    try {
      return format(new Date(iso), "dd/MM/yyyy 'às' HH:mm");
    } catch {
      return iso;
    }
  };

  return (
    <AdminLayout title="Galeria de Fotos">
      {/* Description */}
      <div className="mb-6 max-w-3xl">
        <p className="text-sm text-[#6B7280]">
          Auditoria de ponto biométrico. Cada foto é capturada na ação de ponto com marca d'água
          e armazenada por <strong>90 dias</strong> conforme RGPD. Após esse prazo é apagada automaticamente.
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white border border-[#E5E7EB] p-4 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-wider mb-2 block">Local</Label>
            <Select value={filterLocation} onValueChange={setFilterLocation}>
              <SelectTrigger data-testid="photos-filter-location">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os locais</SelectItem>
                {locations.map((loc) => (
                  <SelectItem key={loc.location_id} value={loc.location_id}>
                    {loc.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider mb-2 block">Funcionário</Label>
            <Select value={filterEmployee} onValueChange={setFilterEmployee}>
              <SelectTrigger data-testid="photos-filter-employee">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {employees.map((emp) => (
                  <SelectItem key={emp.employee_id} value={emp.employee_id}>
                    {emp.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider mb-2 block">Ação</Label>
            <Select value={filterAction} onValueChange={setFilterAction}>
              <SelectTrigger data-testid="photos-filter-action">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                <SelectItem value="clock_in">Entrada</SelectItem>
                <SelectItem value="clock_out">Saída</SelectItem>
                <SelectItem value="break_start">Início Pausa</SelectItem>
                <SelectItem value="break_end">Fim Pausa</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider mb-2 block">De</Label>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              data-testid="photos-filter-start-date"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider mb-2 block">Até</Label>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              data-testid="photos-filter-end-date"
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-3 mt-4">
          <Button
            onClick={fetchPhotos}
            disabled={loading}
            data-testid="photos-refresh-btn"
            className="bg-[#111111] text-white hover:bg-[#1F1F1F]"
          >
            <Search className="w-4 h-4 mr-2" />
            {loading ? 'Buscando...' : 'Atualizar'}
          </Button>
          <Button
            onClick={handleCleanup}
            variant="outline"
            data-testid="photos-cleanup-btn"
            className="border-[#DC2626] text-[#DC2626] hover:bg-[#DC2626] hover:text-white"
          >
            <Eraser className="w-4 h-4 mr-2" />
            Executar limpeza RGPD (90 dias)
          </Button>
          <span className="text-sm text-[#6B7280] self-center" data-testid="photos-count">
            {photos.length} foto(s)
          </span>
        </div>
      </div>

      {/* Gallery */}
      {loading && photos.length === 0 ? (
        <div className="text-center py-12 text-[#6B7280]">Carregando...</div>
      ) : photos.length === 0 ? (
        <div className="bg-white border border-[#E5E7EB] p-12 text-center" data-testid="photos-empty">
          <ImageIcon className="w-12 h-12 mx-auto mb-3 text-[#9CA3AF]" />
          <p className="text-sm text-[#6B7280]">Nenhuma foto encontrada para os filtros selecionados.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
          {photos.map((p) => (
            <div
              key={p.photo_id}
              className="bg-white border border-[#E5E7EB] hover:border-[#111111] transition-colors group"
              data-testid={`photo-card-${p.photo_id}`}
            >
              <button
                type="button"
                className="block w-full aspect-square overflow-hidden bg-[#F3F4F6]"
                onClick={() => setPreviewPhoto(p)}
                aria-label="Ver foto em tamanho grande"
              >
                {p.photo_b64 ? (
                  <img
                    src={p.photo_b64.startsWith('data:') ? p.photo_b64 : `data:image/jpeg;base64,${p.photo_b64}`}
                    alt={`${p.employee_name} - ${ACTION_LABELS[p.action] || p.action}`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[#9CA3AF]">
                    <Camera className="w-8 h-8" />
                  </div>
                )}
              </button>
              <div className="p-3 space-y-1">
                <div className="text-xs font-bold truncate" title={p.employee_name}>
                  {p.employee_name}
                </div>
                <div className="text-[10px] uppercase tracking-wider text-[#6B7280]">
                  {ACTION_LABELS[p.action] || p.action}
                </div>
                <div className="text-[10px] text-[#9CA3AF] truncate" title={p.location_name}>
                  {p.location_name}
                </div>
                <div className="text-[10px] text-[#9CA3AF]">{formatDate(p.created_at)}</div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleDelete(p.photo_id)}
                  data-testid={`photo-delete-${p.photo_id}`}
                  className="w-full h-7 text-[10px] text-[#DC2626] hover:bg-[#FEE2E2] hover:text-[#DC2626] mt-1"
                >
                  <Trash2 className="w-3 h-3 mr-1" />
                  Apagar
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Preview modal */}
      <Dialog open={!!previewPhoto} onOpenChange={(open) => !open && setPreviewPhoto(null)}>
        <DialogContent className="max-w-3xl" data-testid="photo-preview-dialog">
          <DialogHeader>
            <DialogTitle>{previewPhoto?.employee_name}</DialogTitle>
            <DialogDescription>
              {previewPhoto && (
                <span>
                  {ACTION_LABELS[previewPhoto.action] || previewPhoto.action} ·{' '}
                  {previewPhoto.location_name} · {formatDate(previewPhoto.created_at)}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          {previewPhoto?.photo_b64 && (
            <div className="bg-black flex items-center justify-center max-h-[70vh] overflow-hidden">
              <img
                src={
                  previewPhoto.photo_b64.startsWith('data:')
                    ? previewPhoto.photo_b64
                    : `data:image/jpeg;base64,${previewPhoto.photo_b64}`
                }
                alt={previewPhoto.employee_name}
                className="max-w-full max-h-[70vh] object-contain"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
